import React, { useRef, useEffect } from 'react';
import { useMobileDetection } from '../hooks/useMobileDetection';

interface InteractiveCard3DProps {
  children: React.ReactNode;
  className?: string;
  isEnabled?: boolean;
  cardRef?: React.RefObject<HTMLDivElement>;
  mobileRandomTilt?: boolean; // モバイルでランダム傾きを適用するか
}

const InteractiveCard3D: React.FC<InteractiveCard3DProps> = ({ 
  children, 
  className = "", 
  isEnabled = true,
  cardRef,
  mobileRandomTilt = false
}) => {
  const internalRef = useRef<HTMLDivElement>(null);
  const isMobile = useMobileDetection();

  useEffect(() => {
    const element = cardRef?.current || internalRef.current;
    if (!isEnabled || !element) return;
    
    // モバイルでランダム傾きを適用
    if (isMobile && mobileRandomTilt) {
      // -5度から5度の範囲でランダムな傾きを生成
      const randomRotateX = (Math.random() - 0.5) * 10; // -5 to 5
      const randomRotateY = (Math.random() - 0.5) * 10; // -5 to 5
      const randomRotateZ = (Math.random() - 0.5) * 4;  // -2 to 2 (微妙な回転)
      
      element.style.transform = `perspective(1000px) rotateX(${randomRotateX}deg) rotateY(${randomRotateY}deg) rotateZ(${randomRotateZ}deg)`;
      element.style.transition = 'transform 0.3s ease-out';
      return; // モバイルの場合はここで終了
    }
    
    // モバイルで通常の3Dエフェクトは無効化
    if (isMobile) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const mouseX = (e.clientX - centerX) / (rect.width / 2);
      const mouseY = (e.clientY - centerY) / (rect.height / 2);
      
      const rotateY = mouseX * 15;
      const rotateX = -mouseY * 15;
      
      const transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
      element.style.transform = transform;
      element.style.transition = 'none';
    };

    const handleMouseEnter = () => {
      element.style.transition = 'none';
    };

    const handleMouseLeave = () => {
      element.style.transition = 'transform 0.5s ease-out';
      element.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg)';
    };

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isEnabled, cardRef, isMobile, mobileRandomTilt]);

  return (
    <div 
      ref={cardRef || internalRef}
      className={className}
      style={{
        transformStyle: 'preserve-3d'
      }}
    >
      {children}
    </div>
  );
};

export default InteractiveCard3D;
